import type { Dispatch, SetStateAction } from "react";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import TemplatesList from "components/financials/modern/templates/TemplatesList";
import type { RefetchFn } from "components/pages/TemplatesGalleryPage/Filters";
import Filters, { QueryVariablesContext } from "components/pages/TemplatesGalleryPage/Filters";
import { Button } from "components/ui/Button";
import { NativeSelect } from "components/ui/Select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/Tabs";
import { TemplatesEnum } from "components/financials/modern/templates/types";
import type { UseTemplatesReturnType } from "components/financials/modern/templates/hooks/useTemplates";
import type { IFinancialsTemplatesQueryVariables } from "components/financials/modern/graphql/__generated__/FinancialsTemplatesQuery.generated";
import type {
  IFinancialsSheetQuery,
  IFinancialsSheetQueryVariables,
} from "components/financials/modern/graphql/__generated__/FinancialsSheetQuery.generated";
import type { ApolloQueryResult } from "@apollo/client";

interface TemplateSheetContentViewProps {
  isDefiningTemplate: boolean;
  selectedTab: TemplatesEnum;
  isSheetOpen: boolean;
  initialVariables: Partial<IFinancialsTemplatesQueryVariables | undefined>;
  systemTemplate: IFinancialsTemplateFieldsFragment | null;
  renderingTemplate: IFinancialsTemplateFieldsFragment | null;
  financialsTemplate: IFinancialsTemplateFieldsFragment | null;
  ownTemplates: UseTemplatesReturnType["ownTemplates"];
  publicTemplates: UseTemplatesReturnType["publicTemplates"];
  starredTemplates: UseTemplatesReturnType["starredTemplates"];
  setIsCreatingTemplate: (value: boolean) => void;
  setSelectedTab: Dispatch<SetStateAction<TemplatesEnum>>;
  setEditingTemplate: (template: IFinancialsTemplateFieldsFragment | null) => void;
  setIsSheetOpen: (value: boolean) => void;
  onChange?: (template: IFinancialsTemplateFieldsFragment | null) => void;
  onRefetchFinancialsTemplate: (
    newVariables: Partial<IFinancialsSheetQueryVariables>,
  ) => Promise<ApolloQueryResult<IFinancialsSheetQuery>>;
}

export default function TemplateSheetContentView({
  isDefiningTemplate,
  selectedTab,
  isSheetOpen,
  initialVariables,
  systemTemplate,
  renderingTemplate,
  financialsTemplate,
  ownTemplates,
  publicTemplates,
  starredTemplates,
  setIsCreatingTemplate,
  setSelectedTab,
  setEditingTemplate,
  setIsSheetOpen,
  onChange,
  onRefetchFinancialsTemplate,
}: TemplateSheetContentViewProps) {
  const memoizedQueryVariables: [
    RefetchFn<IFinancialsTemplatesQueryVariables>,
    Partial<IFinancialsTemplatesQueryVariables | undefined>,
    IFinancialsTemplatesQueryVariables | undefined,
  ] = useMemo(
    () => [publicTemplates.refetch, initialVariables, publicTemplates.variables],
    [publicTemplates.refetch, initialVariables, publicTemplates.variables],
  );

  if (isDefiningTemplate) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <Button
          size="full"
          variant="ghost"
          className="bg-indigo-50 text-indigo-600 hover:opacity-80 dark:bg-indigo-800/20 dark:text-indigo-200"
          onClick={() => setIsCreatingTemplate(true)}
        >
          <FormattedMessage defaultMessage="Criar novo template" id="Jq5BJN" />
        </Button>
      </div>

      <div>
        <Tabs
          className="w-full space-y-4 rounded-xl font-normal"
          defaultValue="ACTIVE"
          value={selectedTab}
          onValueChange={(tab) => {
            if (tab === "ACTIVE" || tab === "ARCHIVED") {
              ownTemplates.refetch();
            }
            if (tab === "STARRED") {
              starredTemplates.refetch();
            }

            setSelectedTab(tab as TemplatesEnum);
          }}
        >
          <TabsList className="hidden w-full flex-1 lg:flex">
            <TabsTrigger className="w-full" value={TemplatesEnum.Public}>
              <FormattedMessage
                defaultMessage="Públicos ({count})"
                id="BG/Hb6"
                values={{
                  count: publicTemplates.totalCount || 0,
                }}
              />
            </TabsTrigger>
            <TabsTrigger className="w-full" value={TemplatesEnum.StarredTemplates}>
              <FormattedMessage
                defaultMessage="Favoritados ({count})"
                id="+CcnmB"
                values={{
                  count: starredTemplates.totalCount || 0,
                }}
              />
            </TabsTrigger>
            <TabsTrigger className="w-full" value={TemplatesEnum.MyTemplates}>
              <FormattedMessage
                defaultMessage="Meus Templates ({count})"
                id="kwc8o0"
                values={{
                  count: ownTemplates.breakdown?.myTemplatesCount || 0,
                }}
              />
            </TabsTrigger>
            <TabsTrigger className="w-full" value={TemplatesEnum.ArchivedTemplates}>
              <FormattedMessage
                defaultMessage="Arquivados ({count})"
                id="PtomVm"
                values={{
                  count: ownTemplates.breakdown?.archivedCount || 0,
                }}
              />
            </TabsTrigger>
          </TabsList>

          <NativeSelect
            value={selectedTab}
            className="w-full sm:hidden"
            onChange={(event) => setSelectedTab(event.target.value as TemplatesEnum)}
          >
            <option value={TemplatesEnum.Public}>
              <FormattedMessage
                defaultMessage="Públicos ({count})"
                id="BG/Hb6"
                values={{
                  count: ownTemplates.breakdown?.publicCount || 0,
                }}
              />
            </option>
            <option value={TemplatesEnum.StarredTemplates}>
              <FormattedMessage
                defaultMessage="Favoritados ({count})"
                id="+CcnmB"
                values={{
                  count: starredTemplates.totalCount || 0,
                }}
              />
            </option>
            <option value={TemplatesEnum.MyTemplates}>
              <FormattedMessage
                defaultMessage="Meus Templates ({count})"
                id="kwc8o0"
                values={{
                  count: ownTemplates.breakdown?.myTemplatesCount || 0,
                }}
              />
            </option>
            <option value={TemplatesEnum.ArchivedTemplates}>
              <FormattedMessage
                defaultMessage="Arquivados ({count})"
                id="PtomVm"
                values={{
                  count: ownTemplates.breakdown?.archivedCount || 0,
                }}
              />
            </option>
          </NativeSelect>

          {[
            {
              templates: publicTemplates.templates,
              tab: TemplatesEnum.Public,
              isLoading: publicTemplates.loading,
              hasNextPage: publicTemplates.pageInfo?.hasNextPage,
              onFetchMore: () => {
                publicTemplates.refetch({
                  cursor: publicTemplates.pageInfo?.cursor,
                });
              },
              onRefetch: () => {
                ownTemplates.refetch();
                setSelectedTab(TemplatesEnum.MyTemplates);
              },
              children: publicTemplates.variables ? (
                <QueryVariablesContext.Provider value={memoizedQueryVariables}>
                  <Filters />
                </QueryVariablesContext.Provider>
              ) : null,
            },
            {
              templates: starredTemplates.templates.filter(({ archivedAt }) => archivedAt === null),
              tab: TemplatesEnum.StarredTemplates,
              isLoading: starredTemplates.loading,
              onRefetch: () => starredTemplates.refetch(),
            },
            {
              templates: ownTemplates.templates.filter(({ archivedAt }) => archivedAt === null),
              tab: TemplatesEnum.MyTemplates,
              isLoading: ownTemplates.loading,
              onRefetch: () => ownTemplates.refetch(),
            },
            {
              templates: ownTemplates.templates.filter(({ archivedAt }) => archivedAt !== null),
              tab: TemplatesEnum.ArchivedTemplates,
              isLoading: ownTemplates.loading,
              onRefetch: () => ownTemplates.refetch(),
            },
          ].map((node) => (
            <TabsContent key={node.tab} className="border-none p-0" value={node.tab}>
              <TemplatesList
                systemTemplate={systemTemplate}
                renderingTemplate={renderingTemplate}
                inheritedTemplate={financialsTemplate}
                templates={node.templates}
                tab={node.tab}
                isLoading={node.isLoading}
                hasNextPage={node.hasNextPage}
                onEditClick={setEditingTemplate}
                onToggleSheetOpen={() => setIsSheetOpen(!isSheetOpen)}
                onRefetch={node.onRefetch}
                onRefetchStarredTemplates={() => starredTemplates.refetch()}
                onFetchMore={node.onFetchMore}
                setSelectedTab={setSelectedTab}
                onApplyTemplate={onChange}
                onRefetchFinancialsTemplate={onRefetchFinancialsTemplate}
              >
                {node.children}
              </TemplatesList>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </>
  );
}
